import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Flag, Image, ShieldOff, Video } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useRange } from '@/lib/useRange';
import { RANGE_LABELS, type RangeKey } from '@/lib/range';
import { Card, CardLabel } from '@/components/ui/card';
import { StatCard } from '@/components/StatCard';
import { DataTable, type Column } from '@/components/DataTable';
import { StackedBarChart } from '@/components/charts/StackedBarChart';
import { PostLightbox, type LightboxPost } from '@/components/PostLightbox';
import { ModerationActions } from '@/components/ModerationActions';
import { SightengineAnalysis } from '@/components/SightengineAnalysis';

function api<T>(path: string): () => Promise<T> {
  return () => apiFetch<T>(path);
}

interface ModerationPostRow {
  _id: string;
  caption: string | null;
  post_type: string;
  status: string;
  review_status: string;
  is_reported: boolean;
  is_hidden?: boolean;
  likes_count: number;
  comments_count: number;
  createdAt: string;
  updatedAt: string;
  thumbnail: string | null;
  mediaUrl: string | null;
  owner: {
    uid: string | null;
    username: string | null;
    name: string | null;
    profilePhoto: string | null;
  };
  reporter: {
    uid: string | null;
    username: string | null;
    name: string | null;
  } | null;
}

interface AuditRow {
  _id: string;
  admin: { uid: string; email: string };
  action: 'approve' | 'reject' | 'unhide';
  targetPostId: string;
  // `status` is optional on older rows (before the 2026-05-23 allowlist bump).
  before: { review_status: string; is_hidden: boolean; status?: string };
  after: { review_status: string; is_hidden: boolean; status?: string };
  reason: string | null;
  noOp: boolean;
  createdAt: string;
}
interface AuditResponse {
  limit: number;
  items: AuditRow[];
}
interface ModerationPostsResponse {
  limit: number;
  total: number;
  items: ModerationPostRow[];
}

type Outcome = 'approved' | 'rejected' | 'pending';
interface OutcomesResponse {
  range: RangeKey;
  outcomes: Outcome[];
  series: Array<{ date: string } & Record<Outcome, number>>;
}

interface UserAccountRow {
  _id: string;
  name: string | null;
  email: string;
  member_number: number | null;
  email_verified: boolean;
  is_archived: boolean;
  is_banned: boolean;
  createdAt: string;
  updatedAt: string;
}
interface UserListResponse {
  limit: number;
  total: number;
  items: UserAccountRow[];
}

interface DeviceRow {
  _id: string;
  deviceAddress: string;
  accountAssociated: {
    uid: string | null;
    email: string | null;
    name: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}
interface DeviceListResponse {
  limit: number;
  total: number;
  items: DeviceRow[];
}

function PostTypeIcon({ type }: { type: string }): JSX.Element {
  if (type === 'clip') return <Video size={14} strokeWidth={1.5} />;
  return <Image size={14} strokeWidth={1.5} />;
}

function ReviewBadge({ status }: { status: string }): JSX.Element {
  const tone =
    status === 'pending'
      ? 'bg-state-warning/15 text-state-warning'
      : status === 'rejected'
        ? 'bg-state-danger/15 text-state-danger'
        : status === 'approved'
          ? 'bg-state-success/10 text-brand-cyan'
          : 'bg-surface-divider/30 text-fg-subtle';
  return (
    <span
      className={cn(
        'inline-block rounded-md px-2 py-0.5 text-xs font-medium capitalize',
        tone,
      )}
    >
      {status}
    </span>
  );
}

function dateCell(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function postCommonColumns(opts: {
  showReporter: boolean;
  onPreview: (r: ModerationPostRow) => void;
  showActions: boolean;
}): Column<ModerationPostRow>[] {
  const cols: Column<ModerationPostRow>[] = [
    {
      key: 'preview',
      header: '',
      width: '60px',
      cell: (r) => (
        <button
          type="button"
          onClick={() => opts.onPreview(r)}
          title="Open full preview"
          className="block h-12 w-12 rounded-md border border-surface-divider bg-surface-raised overflow-hidden hover:border-fg/50 transition-colors"
        >
          {r.thumbnail ? (
            <img
              src={r.thumbnail}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="block h-full w-full" />
          )}
        </button>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      cell: (r) => (
        <div className="flex items-center gap-2 min-w-0">
          {r.owner.profilePhoto ? (
            <img
              src={r.owner.profilePhoto}
              alt=""
              className="h-6 w-6 rounded-full object-cover border border-surface-divider"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="h-6 w-6 rounded-full bg-surface-raised border border-surface-divider" />
          )}
          <span className="font-medium truncate">
            {r.owner.username ?? r.owner.name ?? '(unknown)'}
          </span>
        </div>
      ),
    },
    {
      key: 'caption',
      header: 'Caption',
      cell: (r) =>
        r.caption?.trim() ? (
          <span className="text-fg-muted">
            {r.caption.length > 56 ? r.caption.slice(0, 56) + '…' : r.caption}
          </span>
        ) : (
          <span className="text-fg-subtle italic">no caption</span>
        ),
    },
    {
      key: 'post_type',
      header: 'Type',
      width: '80px',
      cell: (r) => (
        <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
          <PostTypeIcon type={r.post_type} />
          {r.post_type}
        </span>
      ),
    },
    {
      key: 'review_status',
      header: 'Review',
      width: '100px',
      cell: (r) => <ReviewBadge status={r.review_status} />,
    },
  ];
  if (opts.showReporter) {
    cols.push({
      key: 'reporter',
      header: 'Reported by',
      width: '140px',
      cell: (r) =>
        r.reporter ? (
          <span className="inline-flex items-center gap-1 text-xs">
            <Flag size={11} className="text-state-warning" />
            {r.reporter.username ?? r.reporter.name ?? '(unknown)'}
          </span>
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    });
  }
  cols.push({
    key: 'createdAt',
    header: 'Posted',
    align: 'right',
    width: '120px',
    cell: (r) => dateCell(r.createdAt),
  });
  if (opts.showActions) {
    cols.push({
      key: 'actions',
      header: '',
      width: '200px',
      cell: (r) => (
        <ModerationActions
          post={{
            _id: r._id,
            review_status: r.review_status,
            is_hidden: r.is_hidden ?? undefined,
          }}
          variant="compact"
        />
      ),
    });
  }
  return cols;
}

const userColumns: Column<UserAccountRow>[] = [
  {
    key: 'member_number',
    header: '#',
    align: 'right',
    width: '60px',
    cell: (r) => r.member_number ?? '—',
  },
  {
    key: 'name',
    header: 'Name',
    cell: (r) =>
      r.name?.trim() ? (
        <span className="font-medium">{r.name}</span>
      ) : (
        <span className="text-fg-subtle italic">no name</span>
      ),
  },
  {
    key: 'email',
    header: 'Email',
    cell: (r) => <span className="text-fg-muted">{r.email}</span>,
  },
  {
    key: 'updatedAt',
    header: 'Updated',
    align: 'right',
    width: '120px',
    cell: (r) => dateCell(r.updatedAt),
  },
];

const deviceColumns: Column<DeviceRow>[] = [
  {
    key: 'deviceAddress',
    header: 'Device',
    cell: (r) => (
      <code className="text-xs text-fg-muted">
        {r.deviceAddress.length > 24
          ? r.deviceAddress.slice(0, 12) + '…' + r.deviceAddress.slice(-8)
          : r.deviceAddress}
      </code>
    ),
  },
  {
    key: 'accountAssociated',
    header: 'Account',
    cell: (r) =>
      r.accountAssociated ? (
        <span>
          <span className="text-fg">{r.accountAssociated.name ?? '—'}</span>{' '}
          <span className="text-fg-muted text-xs">
            ({r.accountAssociated.email ?? 'no email'})
          </span>
        </span>
      ) : (
        <span className="text-fg-subtle">—</span>
      ),
  },
  {
    key: 'updatedAt',
    header: 'Banned',
    align: 'right',
    width: '140px',
    cell: (r) => dateCell(r.updatedAt),
  },
];

export function Moderation(): JSX.Element {
  const [range] = useRange();
  const [lightboxPost, setLightboxPost] = useState<LightboxPost | null>(null);
  const errMsg = (q: { error: unknown }): string | null =>
    q.error ? (q.error as Error).message : null;

  function openPreview(r: ModerationPostRow): void {
    setLightboxPost({
      _id: r._id,
      caption: r.caption,
      post_type: r.post_type,
      status: r.status,
      review_status: r.review_status,
      is_hidden: r.is_hidden,
      is_reported: r.is_reported,
      likes_count: r.likes_count,
      comments_count: r.comments_count,
      createdAt: r.createdAt,
      thumbnail: r.thumbnail,
      // For clips the server resolves this to post_image_video_links[1]
      // (the .mov video file). For photos it's the image itself.
      mediaUrl: r.mediaUrl,
      owner: r.owner,
    });
  }

  const reported = useQuery({
    queryKey: ['metrics', 'moderation', 'reported', 50],
    queryFn: api<ModerationPostsResponse>(
      '/api/metrics/moderation/reported?limit=50',
    ),
  });
  const autoRejected = useQuery({
    queryKey: ['metrics', 'moderation', 'auto-rejected', 50],
    queryFn: api<ModerationPostsResponse>(
      '/api/metrics/moderation/auto-rejected?limit=50',
    ),
  });
  const outcomes = useQuery({
    queryKey: ['metrics', 'moderation', 'outcomes', range],
    queryFn: api<OutcomesResponse>(
      `/api/metrics/moderation/outcomes?range=${range}`,
    ),
  });
  const banned = useQuery({
    queryKey: ['metrics', 'moderation', 'banned-users'],
    queryFn: api<UserListResponse>('/api/metrics/moderation/banned-users'),
  });
  const archived = useQuery({
    queryKey: ['metrics', 'moderation', 'archived-users'],
    queryFn: api<UserListResponse>('/api/metrics/moderation/archived-users'),
  });
  const devices = useQuery({
    queryKey: ['metrics', 'moderation', 'banned-devices'],
    queryFn: api<DeviceListResponse>(
      '/api/metrics/moderation/banned-devices',
    ),
  });
  const audit = useQuery({
    queryKey: ['metrics', 'moderation', 'audit-log', 50],
    queryFn: api<AuditResponse>('/api/moderation/audit-log?limit=50'),
  });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="mb-1 text-2xl font-bold">Moderation</h1>
        <p className="text-sm text-fg-muted">
          <span className="text-fg">Reported</span>{' '}
          (user-flagged posts),{' '}
          <span className="text-fg">Auto-rejected</span> (Sightengine blocked
          on upload — content never reached users). Outcomes chart uses the
          range picker ({RANGE_LABELS[range]}); queues reflect current state.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Reported posts"
          value={reported.data?.total}
          loading={reported.isLoading}
          error={errMsg(reported)}
          hint="is_reported=true (history)"
        />
        <StatCard
          label="Auto-rejected"
          value={autoRejected.data?.total}
          loading={autoRejected.isLoading}
          error={errMsg(autoRejected)}
          hint="Sightengine on upload"
        />
        <StatCard
          label="Banned users"
          value={banned.data?.total}
          loading={banned.isLoading}
          error={errMsg(banned)}
        />
        <StatCard
          label="Archived users"
          value={archived.data?.total}
          loading={archived.isLoading}
          error={errMsg(archived)}
          hint="banned excluded"
        />
      </div>

      {/* Reported posts */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between">
          <div>
            <CardLabel>Reported posts</CardLabel>
            <div className="text-lg font-bold mt-1">
              Posts users flagged · most recent first
            </div>
          </div>
          {reported.data && (
            <div className="text-xs text-fg-subtle">
              {reported.data.items.length} of {reported.data.total}
            </div>
          )}
        </div>
        <DataTable
          columns={postCommonColumns({
            showReporter: true,
            onPreview: openPreview,
            showActions: true,
          })}
          rows={reported.data?.items}
          loading={reported.isLoading}
          error={errMsg(reported)}
          empty="No posts have been reported."
          rowKey={(r) => r._id}
        />
      </Card>

      {/* Auto-rejected by Sightengine */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between">
          <div className="flex items-center gap-2">
            <ShieldOff size={16} className="text-state-danger" />
            <div>
              <CardLabel>Auto-rejected · Sightengine</CardLabel>
              <div className="text-lg font-bold mt-1">
                Posts blocked on upload — never reached users
              </div>
              <div className="text-xs text-fg-subtle mt-1">
                Categories scanned: nudity, violence, weapons, gore, drugs,
                offensive content. Raw scores aren't stored — only the final
                reject/approve decision.
              </div>
            </div>
          </div>
          {autoRejected.data && (
            <div className="text-xs text-fg-subtle">
              {autoRejected.data.items.length} of {autoRejected.data.total}
            </div>
          )}
        </div>
        <DataTable
          columns={postCommonColumns({
            showReporter: false,
            onPreview: openPreview,
            showActions: true,
          })}
          rows={autoRejected.data?.items}
          loading={autoRejected.isLoading}
          error={errMsg(autoRejected)}
          empty="Nothing has been auto-rejected by Sightengine."
          rowKey={(r) => r._id}
        />
      </Card>

      {/* Outcomes chart */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Review outcomes over time</CardLabel>
          <div className="text-lg font-bold mt-1">
            Daily counts · approved / rejected / pending · grouped by post creation date
          </div>
        </div>
        {outcomes.isLoading ? (
          <div className="h-[280px] animate-pulse rounded-md bg-surface-divider/30" />
        ) : outcomes.error ? (
          <div className="text-sm text-state-danger">{errMsg(outcomes)}</div>
        ) : (
          <StackedBarChart
            data={outcomes.data?.series ?? []}
            series={[
              { key: 'approved', label: 'Approved', color: '#19D5F1' },
              { key: 'pending', label: 'Pending', color: '#FFB020' },
              { key: 'rejected', label: 'Rejected', color: '#FF0034' },
            ]}
          />
        )}
      </Card>

      {/* Banned + archived users */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Banned users</CardLabel>
            <div className="text-lg font-bold mt-1">
              Currently banned · most recent first
            </div>
          </div>
          <DataTable
            columns={userColumns}
            rows={banned.data?.items}
            loading={banned.isLoading}
            error={errMsg(banned)}
            empty="No banned users."
            rowKey={(r) => r._id}
          />
        </Card>
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Archived users</CardLabel>
            <div className="text-lg font-bold mt-1">
              Soft-deleted accounts · most recent first
            </div>
          </div>
          <DataTable
            columns={userColumns}
            rows={archived.data?.items}
            loading={archived.isLoading}
            error={errMsg(archived)}
            empty="No archived users."
            rowKey={(r) => r._id}
          />
        </Card>
      </div>

      {/* Banned devices */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Banned devices</CardLabel>
          <div className="text-lg font-bold mt-1">
            Device-level bans applied via Malsi's `devices.isBanned`
          </div>
        </div>
        <DataTable
          columns={deviceColumns}
          rows={devices.data?.items}
          loading={devices.isLoading}
          error={errMsg(devices)}
          empty="No banned devices."
          rowKey={(r) => r._id}
        />
      </Card>

      {/* Audit log */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Admin action audit log</CardLabel>
          <div className="text-lg font-bold mt-1">
            Every approve / reject / unhide performed from this dashboard
          </div>
          <div className="text-xs text-fg-subtle mt-1">
            Written to <code>malsi-prod.admin_actions</code>. Includes no-op
            actions (audit trail of attempts) and the before/after state of
            each affected post.
          </div>
        </div>
        <DataTable
          columns={[
            {
              key: 'createdAt',
              header: 'When',
              width: '160px',
              cell: (r) => dateCell(r.createdAt),
            },
            {
              key: 'admin',
              header: 'Admin',
              width: '200px',
              cell: (r) => (
                <span className="text-fg-muted">{r.admin.email}</span>
              ),
            },
            {
              key: 'action',
              header: 'Action',
              width: '110px',
              cell: (r) => (
                <span
                  className={cn(
                    'inline-block rounded-md px-2 py-0.5 text-xs font-medium capitalize',
                    r.action === 'approve' &&
                      'bg-state-success/10 text-brand-cyan',
                    r.action === 'reject' &&
                      'bg-state-danger/15 text-state-danger',
                    r.action === 'unhide' &&
                      'bg-surface-divider/30 text-fg-muted',
                  )}
                >
                  {r.action}
                  {r.noOp && (
                    <span className="ml-1 text-fg-subtle">(no-op)</span>
                  )}
                </span>
              ),
            },
            {
              key: 'change',
              header: 'Change',
              cell: (r) => {
                const reviewChanged =
                  r.before.review_status !== r.after.review_status;
                const hiddenChanged =
                  r.before.is_hidden !== r.after.is_hidden;
                const statusChanged =
                  r.before.status !== undefined &&
                  r.after.status !== undefined &&
                  r.before.status !== r.after.status;
                return (
                  <span className="text-xs text-fg-muted font-mono">
                    <span className={reviewChanged ? 'text-fg' : ''}>
                      review: {r.before.review_status} →{' '}
                      {r.after.review_status}
                    </span>
                    {' · '}
                    <span className={hiddenChanged ? 'text-fg' : ''}>
                      hidden: {String(r.before.is_hidden)} →{' '}
                      {String(r.after.is_hidden)}
                    </span>
                    {statusChanged && (
                      <>
                        {' · '}
                        <span className="text-fg">
                          status: {r.before.status} → {r.after.status}
                        </span>
                      </>
                    )}
                  </span>
                );
              },
            },
            {
              key: 'reason',
              header: 'Reason',
              cell: (r) =>
                r.reason ? (
                  <span className="text-fg-muted italic">{r.reason}</span>
                ) : (
                  <span className="text-fg-subtle">—</span>
                ),
            },
            {
              key: 'targetPostId',
              header: 'Post',
              width: '130px',
              cell: (r) => (
                <code className="text-xs text-fg-subtle">
                  {r.targetPostId.slice(0, 8)}…
                </code>
              ),
            },
          ]}
          rows={audit.data?.items}
          loading={audit.isLoading}
          error={errMsg(audit)}
          empty="No moderation actions yet."
          rowKey={(r) => r._id}
        />
      </Card>

      {/* Lightbox — opens when a thumbnail is clicked anywhere on this page */}
      <PostLightbox
        post={lightboxPost}
        onClose={() => setLightboxPost(null)}
        analysis={
          lightboxPost &&
          (lightboxPost.post_type === 'photo' ||
            lightboxPost.post_type === 'clip') ? (
            <SightengineAnalysis postId={lightboxPost._id} />
          ) : null
        }
        actions={
          lightboxPost && (
            <ModerationActions
              post={{
                _id: lightboxPost._id,
                review_status: lightboxPost.review_status,
                is_hidden: lightboxPost.is_hidden,
              }}
              variant="full"
              onDone={() => setLightboxPost(null)}
            />
          )
        }
      />
    </div>
  );
}
